/**
 * Ovatime Chatbot Widget — vanilla JS, ~5 KB minified
 *
 * Embed in Shopify theme.liquid before </body>:
 *   <script defer src="https://ovatime-bot.apexautomate.de/widget.js"
 *           data-webhook="https://ovatime.app.n8n.cloud/webhook/ovatime-chat"
 *           data-locale="auto"></script>
 *
 * Visibility: gated by Liquid template — show on home/collection/product, hide on cart/checkout.
 */
(function () {
  "use strict";
  if (window.__ovatimeWidgetMounted) return;
  window.__ovatimeWidgetMounted = true;

  // ── Config ────────────────────────────────────────────────────────────────
  const script = document.currentScript || document.querySelector('script[src*="widget.js"]');
  const WEBHOOK = (script && script.dataset.webhook) || "";
  const LOCALE = (script && script.dataset.locale) || "auto";
  const BUBBLE_DELAY_MS = 3000;

  if (!WEBHOOK) {
    console.warn("[Ovatime] No data-webhook attribute on script tag");
    return;
  }

  // ── Session ───────────────────────────────────────────────────────────────
  // Wrapped in try/catch because Safari Private Mode and some content-blocker
  // configurations throw on localStorage access — without the catch the whole
  // widget IIFE would die here and the bubble would never mount.
  const SESSION_KEY = "ovatime_session_id";
  let sessionId = null;
  try { sessionId = localStorage.getItem(SESSION_KEY); } catch (e) { /* private mode */ }
  if (!sessionId) {
    sessionId = "s_" + Date.now() + "_" + Math.random().toString(36).slice(2, 10);
    try { localStorage.setItem(SESSION_KEY, sessionId); } catch (e) { /* ignore */ }
  }

  // ── State ─────────────────────────────────────────────────────────────────
  let isOpen = false;
  let isSending = false;
  let detectedLang = LOCALE === "en" ? "en" : "de"; // updated by first response

  const STR = {
    de: {
      greeting: "Hey! Ich bin der KI-Berater von OVATIME. Wie kann ich dir helfen?",
      placeholder: "Stell deine Frage…",
      send: "Senden",
      error: "Hoppla, da ist was schiefgelaufen. Bitte versuch's gleich nochmal.",
      typing: "Berater tippt…",
      chips: ["Was ist The Golden One?", "Welches Produkt für mehr Energie?", "Ich bin schon Kunde"]
    },
    en: {
      greeting: "Hey! I'm OVATIME's AI advisor. How can I help?",
      placeholder: "Ask a question…",
      send: "Send",
      error: "Something went wrong. Please try again in a moment.",
      typing: "Advisor is typing…",
      chips: ["What is The Golden One?", "Which product for more energy?", "I'm an existing customer"]
    }
  };
  // Progressive typing labels — eased the "is this dead?" feeling on cold-cache
  // requests where the first response can take 30-60s. Each stage replaces the
  // label after its delay; dots keep animating throughout.
  const TYPING_STAGES = {
    de: [
      { ms: 0, text: "Berater tippt" },
      { ms: 5000, text: "Durchsuche Wissensdatenbank" },
      { ms: 15000, text: "Stelle Antwort zusammen" },
      { ms: 30000, text: "Einen Moment noch — fast fertig" }
    ],
    en: [
      { ms: 0, text: "Advisor is typing" },
      { ms: 5000, text: "Searching knowledge base" },
      { ms: 15000, text: "Composing response" },
      { ms: 30000, text: "Hang tight — almost there" }
    ]
  };
  let typingTimers = [];
  const t = (k) => (STR[detectedLang] || STR.de)[k];

  // ── Styles (injected once) ────────────────────────────────────────────────
  const css = `
    .ot-bubble{position:fixed;bottom:320px;right:20px;width:56px;height:56px;border-radius:50%;background:#000000;color:#fff;border:0;cursor:pointer;box-shadow:0 4px 16px rgba(0,0,0,.18);z-index:2147483647;display:flex;align-items:center;justify-content:center;transition:transform .15s ease;font:600 13px/1 -apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif}
    .ot-badge{position:absolute;top:-2px;right:-2px;min-width:20px;height:20px;padding:0 5px;border-radius:10px;background:#e53e3e;color:#fff;font:700 12px/20px -apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;display:none;align-items:center;justify-content:center;box-shadow:0 0 0 2px #fff;pointer-events:none;text-align:center}
    .ot-badge.ot-show{display:flex;animation:ot-badge-blink 1.1s ease-in-out infinite}
    @keyframes ot-badge-blink{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.35;transform:scale(.88)}}
    .ot-bubble:hover{transform:scale(1.05)}
    .ot-bubble svg{width:26px;height:26px;fill:#fff}
    .ot-panel{position:fixed;bottom:20px;right:20px;width:380px;max-width:calc(100vw - 40px);height:560px;max-height:calc(100vh - 40px);background:#fff;border-radius:14px;box-shadow:0 12px 40px rgba(0,0,0,.18);z-index:2147483647;display:flex;flex-direction:column;overflow:hidden;font:14px/1.5 -apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;color:#000000}
    .ot-panel.ot-hidden{display:none}
    .ot-header{padding:14px 16px;background:#000000;color:#fff;display:flex!important;justify-content:space-between;align-items:center;font-weight:600;width:100%!important;box-sizing:border-box;flex-shrink:0}
    .ot-header>span{flex:1 1 auto;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
    .ot-close{background:0;border:0;color:#fff;cursor:pointer;font-size:22px;line-height:1;opacity:.7;width:32px!important;height:32px!important;min-width:32px!important;display:flex;align-items:center;justify-content:center;padding:0;flex-shrink:0;margin-left:8px;box-sizing:border-box}
    .ot-close:hover{opacity:1}
    .ot-msgs{flex:1;overflow-y:auto;padding:14px;background:#fafafa}
    .ot-msg{margin-bottom:12px;max-width:85%;padding:10px 14px;border-radius:14px;overflow-wrap:break-word;word-break:normal;white-space:pre-wrap}
    .ot-msg.ot-user{background:#000000;color:#fff;margin-left:auto;border-bottom-right-radius:4px}
    .ot-msg.ot-bot{background:#fff;border:1px solid #e5e5e5;border-bottom-left-radius:4px}
    .ot-msg a{color:#000000;text-decoration:underline}
    .ot-msg strong{font-weight:600}
    .ot-msg ul{margin:6px 0;padding-left:20px}
    .ot-msg li{margin:2px 0}
    .ot-typing{display:flex;align-items:center;gap:8px;padding:10px 14px;background:#fff;border:1px solid #e5e5e5;border-radius:14px;border-bottom-left-radius:4px;width:fit-content;margin-bottom:12px;max-width:85%}
    .ot-typing-text{font-size:12px;color:#666;font-style:italic}
    .ot-typing-dots{display:flex;gap:4px}
    .ot-typing-dots span{width:6px;height:6px;border-radius:50%;background:#999;animation:ot-blink 1.2s infinite}
    .ot-typing-dots span:nth-child(2){animation-delay:.2s}
    .ot-typing-dots span:nth-child(3){animation-delay:.4s}
    @keyframes ot-blink{0%,80%,100%{opacity:.2}40%{opacity:1}}
    .ot-chips{display:flex;flex-wrap:wrap;gap:6px;margin-top:8px}
    .ot-chip{padding:6px 12px;background:#fff;border:1px solid #d4d4d8;border-radius:16px;font-size:12px;cursor:pointer;color:#000000;white-space:normal!important;text-align:left;line-height:1.3;max-width:100%!important;width:auto!important;box-sizing:border-box;overflow-wrap:break-word}
    .ot-chip:hover{background:#f4f4f5}
    .ot-input-row{display:flex!important;border-top:1px solid #e5e5e5;background:#fff;align-items:stretch;width:100%!important;box-sizing:border-box}
    /* IMPORTANT — font-size must be 16px+ to prevent iOS Safari's auto-zoom
       on focus. Anything smaller and iOS zooms in ~14%, which makes the
       panel exceed the viewport and clips the right side (header close
       button, chip text, Senden button). This is the *real* root cause of
       the iOS clipping issue Chris flagged; everything else was a symptom. */
    .ot-input{flex:1 1 auto!important;min-width:0!important;width:auto!important;max-width:none!important;border:0!important;padding:14px;font-size:16px;font-family:inherit;outline:0;color:#000000;box-sizing:border-box;background:#fff}
    .ot-send{flex:0 0 auto!important;background:#000000;color:#fff;border:0!important;padding:0 18px;cursor:pointer;font:600 13px/1 inherit;min-width:64px!important;width:auto!important;max-width:none!important;display:flex;align-items:center;justify-content:center;gap:6px;box-sizing:border-box}
    .ot-send svg{width:18px;height:18px;fill:currentColor;display:none}
    .ot-send:disabled{opacity:.4;cursor:not-allowed}
    .ot-disclaimer{padding:8px 14px;font-size:11px;color:#888;background:#fafafa;border-top:1px solid #f0f0f0;text-align:center}
    /* Mobile + tablet portrait — fullscreen panel, compact send, safe-area-aware launcher.
       Raised from 480px to 768px so larger phones (Pro Max landscape) and tablet portrait
       get the same fullscreen treatment instead of the cramped 380px desktop layout. */
    @media (max-width:768px){
      /* Floor of 80px lifts the bubble above Shopify's preview bar and most
         cookie banners without needing JS detection. JS scan below can push
         higher if a sticky element is taller than that, but it can't go lower. */
      .ot-bubble{bottom:max(80px, calc(env(safe-area-inset-bottom) + 80px))!important;right:14px!important;left:auto!important}
      /* !important everywhere on the mobile panel rule because the base rule's
         right:20px / width:380px / max-width:calc(...) was winning on iOS Safari
         in some cases — possibly cache or cascade quirk. Force every property
         so the panel always fills the viewport on mobile. */
      .ot-panel{position:fixed!important;top:0!important;bottom:0!important;left:0!important;right:0!important;width:auto!important;max-width:none!important;min-width:0!important;height:auto!important;min-height:0!important;max-height:none!important;border-radius:0!important;padding-bottom:env(safe-area-inset-bottom);box-sizing:border-box}
      .ot-input-row{padding-bottom:env(safe-area-inset-bottom)}
      .ot-msg{max-width:92%}
      /* Compact send button — icon visible, label hidden, ~48px wide instead of 64px+. */
      .ot-send{padding:0 14px;min-width:48px}
      .ot-send-label{display:none}
      .ot-send svg{display:inline-block}
    }
  `;
  const styleEl = document.createElement("style");
  styleEl.textContent = css;
  document.head.appendChild(styleEl);

  // ── DOM ───────────────────────────────────────────────────────────────────
  const bubble = document.createElement("button");
  bubble.className = "ot-bubble";
  bubble.setAttribute("aria-label", "Open Ovatime chat");
  bubble.innerHTML =
    '<svg viewBox="0 0 24 24"><path d="M12 3C7 3 3 6.6 3 11c0 2 .9 3.9 2.4 5.3-.1 1-.5 2.6-1.6 3.5 0 0 2.5-.2 4.4-1.7C9.3 18.7 10.6 19 12 19c5 0 9-3.6 9-8s-4-8-9-8z"/></svg>' +
    '<span class="ot-badge" id="ot-badge" aria-hidden="true">1</span>';
  bubble.style.display = "none";

  const panel = document.createElement("div");
  panel.className = "ot-panel ot-hidden";
  panel.innerHTML =
    '<div class="ot-header"><span>OVATIME</span><button class="ot-close" aria-label="Close">×</button></div>' +
    '<div class="ot-msgs" id="ot-msgs"></div>' +
    '<div class="ot-input-row">' +
    '<input class="ot-input" id="ot-input" placeholder="' + t("placeholder") + '" autocomplete="off" />' +
    '<button class="ot-send" id="ot-send" aria-label="' + t("send") + '">' +
      '<span class="ot-send-label">' + t("send") + '</span>' +
      '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M2 21l21-9L2 3v7l15 2-15 2v7z"/></svg>' +
    "</button>" +
    "</div>" +
    '<div class="ot-disclaimer">Hinweis: Dieser Chatbot ersetzt keine medizinische Beratung. Bei gesundheitlichen Fragen wende dich bitte an deinen Arzt.</div>';

  document.body.appendChild(bubble);
  document.body.appendChild(panel);

  const msgs = panel.querySelector("#ot-msgs");
  const input = panel.querySelector("#ot-input");
  const sendBtn = panel.querySelector("#ot-send");

  // ── Markdown (minimal: **bold**, [link](url), bullet lists, newlines) ────
  function escapeHtml(s) {
    return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
  }
  function renderMarkdown(s) {
    let html = escapeHtml(s);

    // Group consecutive `- ` lines into <ul><li>...</li></ul>. Done before
    // the newline→<br> pass so list items don't get a stray <br> between them.
    const lines = html.split("\n");
    const out = [];
    let inList = false;
    for (const line of lines) {
      const m = line.match(/^- (.+)$/);
      if (m) {
        if (!inList) { out.push("<ul>"); inList = true; }
        out.push("<li>" + m[1] + "</li>");
      } else {
        if (inList) { out.push("</ul>"); inList = false; }
        out.push(line);
      }
    }
    if (inList) out.push("</ul>");
    html = out.join("\n");

    html = html
      .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
      // Markdown links first — `[text](url)` becomes proper <a>
      .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>')
      // Auto-link any remaining bare URL. The negative lookbehind `(?<!href=")`
      // skips URLs already inside an <a> tag we just generated above. Trailing
      // punctuation `.,;)` is excluded so "see https://x.com." doesn't link the
      // period.
      .replace(/(?<!href=")(\bhttps?:\/\/[^\s<]+?)(?=[.,;)\s]|$)/g, '<a href="$1" target="_blank" rel="noopener">$1</a>')
      .replace(/\n/g, "<br>")
      // Strip <br>s that wound up immediately around block list markers — they
      // render as awkward extra blank lines next to <ul>/<li>.
      .replace(/<br>(<\/?(?:ul|li)[^>]*>)/g, "$1")
      .replace(/(<\/?(?:ul|li)[^>]*>)<br>/g, "$1");

    return html;
  }

  // ── UI helpers ────────────────────────────────────────────────────────────
  function appendMsg(role, text) {
    const el = document.createElement("div");
    el.className = "ot-msg ot-" + role;
    el.innerHTML = role === "bot" ? renderMarkdown(text) : escapeHtml(text);
    msgs.appendChild(el);
    msgs.scrollTop = msgs.scrollHeight;
    return el;
  }
  function appendTyping() {
    const el = document.createElement("div");
    el.className = "ot-typing";
    el.id = "ot-typing-indicator";
    el.innerHTML =
      '<span class="ot-typing-text"></span>' +
      '<span class="ot-typing-dots"><span></span><span></span><span></span></span>';
    msgs.appendChild(el);
    msgs.scrollTop = msgs.scrollHeight;

    const stages = TYPING_STAGES[detectedLang] || TYPING_STAGES.de;
    const textEl = el.querySelector(".ot-typing-text");
    stages.forEach((stage) => {
      const t = setTimeout(() => {
        if (textEl) textEl.textContent = stage.text;
      }, stage.ms);
      typingTimers.push(t);
    });
  }
  function removeTyping() {
    typingTimers.forEach(clearTimeout);
    typingTimers = [];
    const el = document.getElementById("ot-typing-indicator");
    if (el) el.remove();
  }
  function appendChips(items, onPick) {
    const wrap = document.createElement("div");
    wrap.className = "ot-chips";
    items.forEach((label) => {
      const b = document.createElement("button");
      b.className = "ot-chip";
      b.textContent = label;
      b.addEventListener("click", () => {
        wrap.remove();
        onPick(label);
      });
      wrap.appendChild(b);
    });
    msgs.appendChild(wrap);
    msgs.scrollTop = msgs.scrollHeight;
  }

  // ── First-visit launcher badge ───────────────────────────────────────────
  // Red "1" appears 3s after the bubble shows, only if this browser hasn't
  // opened the chat before. Disappears permanently once they open it.
  const BADGE_SEEN_KEY = "ovatime_chat_opened";
  const BADGE_EXTRA_DELAY_MS = 3000;
  const badge = bubble.querySelector("#ot-badge");
  function isFirstVisit() {
    try { return localStorage.getItem(BADGE_SEEN_KEY) !== "1"; }
    catch (e) { return true; } // localStorage blocked (private mode) → still show
  }
  function maybeShowBadge() {
    if (!isFirstVisit()) return;
    setTimeout(() => {
      if (isOpen) return; // user already opened it before delay elapsed
      badge.classList.add("ot-show");
    }, BADGE_EXTRA_DELAY_MS);
  }
  function dismissBadge() {
    badge.classList.remove("ot-show");
    try { localStorage.setItem(BADGE_SEEN_KEY, "1"); } catch (e) {}
  }

  // ── Open / close ──────────────────────────────────────────────────────────
  function open() {
    if (isOpen) return;
    isOpen = true;
    dismissBadge();
    // Re-append to body so we're last in DOM. Trusted Shops mounts after our
    // widget on page load, and with tied z-indexes the later element wins.
    // Re-appending on open puts our panel back on top of TS.
    document.body.appendChild(panel);
    panel.classList.remove("ot-hidden");
    // Hide the bubble while the panel is open — both live at right:20px and
    // the bubble would otherwise show through the panel's right edge.
    bubble.style.display = "none";
    // Re-scan for sticky elements (Shopify draft bar, cookie banners) and
    // apply the offset now, in case the user opened the chat before the
    // periodic scan ran.
    if (typeof adjustBubbleBottom === "function") adjustBubbleBottom();
    if (msgs.childElementCount === 0) {
      appendMsg("bot", t("greeting"));
      appendChips(t("chips"), (label) => sendMessage(label));
    }
    setTimeout(() => input.focus(), 100);
  }
  function close() {
    isOpen = false;
    panel.classList.add("ot-hidden");
    bubble.style.display = "flex";
  }
  bubble.addEventListener("click", open);
  panel.querySelector(".ot-close").addEventListener("click", close);

  // ── Send ──────────────────────────────────────────────────────────────────
  async function sendMessage(text) {
    text = (text || "").trim();
    if (!text || isSending) return;
    isSending = true;
    sendBtn.disabled = true;
    input.value = "";

    appendMsg("user", text);
    appendTyping();

    try {
      const res = await fetch(WEBHOOK, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text })
      });
      removeTyping();
      if (!res.ok) {
        appendMsg("bot", t("error"));
        return;
      }
      const data = await res.json();
      if (data.language && data.language !== detectedLang) {
        detectedLang = data.language;
        input.placeholder = t("placeholder");
        // Update label span only — don't overwrite the icon SVG.
        const lbl = sendBtn.querySelector(".ot-send-label");
        if (lbl) lbl.textContent = t("send");
        sendBtn.setAttribute("aria-label", t("send"));
      }
      appendMsg("bot", data.response || t("error"));
    } catch (e) {
      removeTyping();
      appendMsg("bot", t("error"));
      console.warn("[Ovatime]", e);
    } finally {
      isSending = false;
      sendBtn.disabled = false;
      input.focus();
    }
  }

  // Prevent the Send button from stealing focus from the input on tap. Without
  // this, on mobile the first tap blurs the input (which dismisses the soft
  // keyboard) and the click never registers on the button — the user has to
  // tap a second time. preventDefault on pointerdown keeps focus on the input
  // so the click fires immediately while the keyboard is still open.
  sendBtn.addEventListener("pointerdown", (e) => e.preventDefault());
  sendBtn.addEventListener("mousedown", (e) => e.preventDefault());
  sendBtn.addEventListener("click", () => sendMessage(input.value));
  input.addEventListener("keydown", (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input.value);
    }
  });

  // ── Mobile keyboard handling ──────────────────────────────────────────────
  // iOS Safari's `100dvh` doesn't shrink when the soft keyboard appears, so
  // the input row + Senden button get pushed below the visible viewport when
  // the user focuses the input. visualViewport.height reports the actual
  // visible area (keyboard subtracted), so we sync the panel height to it.
  function applyMobileViewport() {
    if (window.innerWidth > 768 || !window.visualViewport) return;
    if (panel.classList.contains("ot-hidden")) return;
    // Detect whether the soft keyboard is actually open. visualViewport.height
    // is smaller than innerHeight when the keyboard takes screen space. Use a
    // 15% margin to ignore tiny URL-bar resize changes that aren't the keyboard.
    const keyboardOpen =
      window.visualViewport.height < window.innerHeight * 0.85;
    if (keyboardOpen) {
      // Constrain panel to the visible-above-keyboard area.
      panel.style.height = window.visualViewport.height + "px";
    } else {
      // Keyboard closed — clear the inline height so CSS top:0 + bottom:NN
      // (set by the sticky-bar scan) defines the panel's vertical span.
      // Otherwise the height would override the bottom anchor and the panel
      // would extend behind a sticky bar (Shopify draft, cookie banner, etc.).
      panel.style.height = "";
    }
  }
  if (window.visualViewport) {
    window.visualViewport.addEventListener("resize", applyMobileViewport);
    window.visualViewport.addEventListener("scroll", applyMobileViewport);
  }
  // Re-apply when the panel opens or the input is focused (covers the case
  // where the keyboard appears after open).
  input.addEventListener("focus", () => setTimeout(applyMobileViewport, 50));
  input.addEventListener("blur", () => {
    // When keyboard closes, drop the inline height so CSS `100dvh` takes over.
    if (window.innerWidth <= 768) panel.style.height = "";
  });

  // ── Sticky-bottom-element detection ──────────────────────────────────────
  // The bubble's CSS `bottom` value only knows about the iOS safe area — it
  // can't account for Shopify's draft preview bar, cookie/consent banners,
  // sticky footers, etc. We can't predict their markup, so we walk every
  // visible fixed-position element on the page and pick the tallest one
  // that's anchored to the viewport bottom, then lift the bubble above it.
  function getBottomStickyHeight() {
    let maxHeight = 0;
    const viewportH = window.innerHeight;
    // 1) Universal scan: walks every visible fixed/sticky element anchored
    // to the viewport bottom. Catches cookie banners, sticky add-to-cart,
    // promo bars, etc.
    const all = document.querySelectorAll("*");
    for (let i = 0; i < all.length; i++) {
      const el = all[i];
      if (el === bubble || el === panel) continue;
      let pos;
      try { pos = getComputedStyle(el).position; } catch (e) { continue; }
      if (pos !== "fixed" && pos !== "sticky") continue;
      const rect = el.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) continue;
      const anchoredBottom = Math.abs(rect.bottom - viewportH) < 10;
      if (!anchoredBottom) continue;
      if (rect.height >= viewportH * 0.6) continue; // skip full-viewport overlays
      if (rect.height > maxHeight) maxHeight = rect.height;
    }
    // 2) Shopify preview / draft bar lives in *closed* Shadow DOM that
    // querySelectorAll can't pierce. Detect it indirectly via URL params or
    // the host element being present and reserve a ~60px offset.
    const url = location.search || "";
    const shopifyPreview =
      url.indexOf("preview_theme_id=") !== -1 ||
      !!document.querySelector("shopify-preview-bar") ||
      !!document.querySelector("[data-shopify-preview-bar]");
    if (shopifyPreview && maxHeight < 60) maxHeight = 60;
    return maxHeight;
  }
  function adjustBubbleBottom() {
    if (window.innerWidth > 768) return; // desktop has its own bottom:320px rule
    const stickyH = getBottomStickyHeight();
    const FLOOR = 80; // matches the CSS floor — never sit lower than 80px
    const baseGap = 14;
    // Bubble: only override CSS if scan found something taller than the 80px floor.
    if (stickyH + baseGap > FLOOR) {
      bubble.style.bottom = (stickyH + baseGap) + "px";
    } else {
      bubble.style.bottom = "";
    }
    // Panel: when the chat is open the input row sits at the bottom of the
    // panel. If the panel extends behind a sticky bar, the input + Senden
    // button get covered. Lift the panel's bottom edge above the sticky bar.
    if (stickyH > 0) {
      panel.style.bottom = stickyH + "px";
    } else {
      panel.style.bottom = "";
    }
  }
  // Initial scan after page settles (gives consent banners time to mount).
  setTimeout(adjustBubbleBottom, 800);
  // Re-scan on resize + every 3s for the first 30s (catches late-loaded
  // banners), then stop. Avoids forever-polling overhead.
  window.addEventListener("resize", adjustBubbleBottom);
  let scanCount = 0;
  const scanInterval = setInterval(() => {
    adjustBubbleBottom();
    if (++scanCount >= 10) clearInterval(scanInterval);
  }, 3000);

  // ── Mount: show bubble after delay, then badge if first visit ────────────
  setTimeout(() => {
    bubble.style.display = "flex";
    maybeShowBadge();
  }, BUBBLE_DELAY_MS);
})();

